/// <amd-module name="Search/Package/Components/ZeroData" />

import * as React from "react";
import * as Constants from "Search/Package/Constants";
import * as Container from "Search/Shared/Base/Container";
import * as Resources from "Search/Package/Resources";
import * as ZeroData from "Search/Shared/Components/ZeroData";
import * as SharedResources from "Search/Shared/Resources";
import * as Utils_Accessibility from 'VSS/Core/Util/Accessibility';
import { ScenarioType } from "Search/Shared/Components/ZeroData/ZeroData.Props";
import { ActionCreator } from "Search/Package/Flux/ActionCreator";
import { StoresHub, IAggregatedState, StoreName } from "Search/Package/Flux/StoresHub"
import { format } from "VSS/Core/Util/String";
import { FormatComponent } from "VSS/Platform/Components/Format";

export const ZeroDataContainer = Container.create<StoreName, ActionCreator, IAggregatedState, StoresHub>(
    [
        "zeroDataStore"
    ],
    ({
        searchStoreState,
        zeroDataState
    }, props) => {
        const { scenario } = zeroDataState;
        if (scenario && scenario !== ZeroData.ScenarioType.None) {
            const { activityId, query, error } = searchStoreState;
            const {
                notifyResultsRendered,
                publishZeroData,
                notifySearchFailed,
                notifyFeedbackMailLinkClicked
            } = props.actionCreator;
            let zeroDataProps = {} as ZeroData.IZeroDataProps;
            if (scenario === ZeroData.ScenarioType.LandingPage) {
                zeroDataProps.message = Resources.PackageWelcomeText;
                zeroDataProps.help = "";
                zeroDataProps.announceMessage = Resources.PackageWelcomeText;
            }
            else {
                zeroDataProps =
                    getZeroDataProps(query.searchText, activityId, scenario, notifyFeedbackMailLinkClicked, props.isHosted);
            }

            return <ZeroData.ZeroData
                {...zeroDataProps}
                pageContext={props.pageContext}
                onDidMount={
                    () => {
                        Utils_Accessibility.announce(zeroDataProps.announceMessage, true);
                        publishZeroData(ZeroData.ScenarioType[scenario], error);
                        onDidMount(scenario, notifyResultsRendered, notifySearchFailed)
                    }
                } />;
        }

        return null;
    });

function onDidMount(
    zeroDataScenario: ZeroData.ScenarioType,
    onResultsRendered: () => void,
    onSearchFailed: () => void): void {
    const searchFailed = zeroDataScenario === ZeroData.ScenarioType.ServiceError;

    if (!searchFailed) {
        onResultsRendered();
    } else {
        onSearchFailed();
    }
}

function getZeroDataProps(
    queryText: string,
    actId: string | undefined,
    scenarioType: ScenarioType,
    onFeedbackMailLinkCliked: () => void,
    isHosted: boolean): ZeroData.IZeroDataProps {
    let zeroDataProps = {} as ZeroData.IZeroDataProps;

    if (scenarioType === ZeroData.ScenarioType.ServiceError) {
        const messageText: string = SharedResources.ServiceErrorMessage;
        const feedbackLink = format(Constants.CodexFeedbackMailToLinkFormat, actId)

        zeroDataProps.scenario = ZeroData.ScenarioType.ServiceError;
        zeroDataProps.message = messageText;
        zeroDataProps.help = ZeroData.getServiceErrorHelp(isHosted, feedbackLink);
        zeroDataProps.announceMessage = SharedResources.ServiceErrorMessage;
    } else {
        zeroDataProps.scenario = scenarioType;
        if (zeroDataProps.scenario === ZeroData.ScenarioType.ZeroResults ||
            zeroDataProps.scenario === ZeroData.ScenarioType.ZeroResultsWithFilter ||
            zeroDataProps.scenario === ZeroData.ScenarioType.ZeroResultsWithWildcardAndFilter) {
            zeroDataProps.message = getZeroDataMessage(queryText, Resources.ZeroPackageResultsMessageFormat);
            let helpMessage: string =
                zeroDataProps.scenario === ZeroData.ScenarioType.ZeroResults ||
                    zeroDataProps.scenario === ZeroData.ScenarioType.ZeroResultsWithFilter ?
                    SharedResources.ZeroResultsWithFilterHelpText : SharedResources.ZeroResultsWithWildcardAndFilterHelpText;
            zeroDataProps.help = getZeroDataHelp(isHosted, helpMessage, actId, onFeedbackMailLinkCliked);
            zeroDataProps.announceMessage = format(Resources.ZeroPackageResultsMessageFormat, queryText);
        } else if (zeroDataProps.scenario === ZeroData.ScenarioType.EmptyQueryNotSupported) {
            zeroDataProps.message = getZeroDataMessage(queryText, SharedResources.EmptyQueryMessage);
            zeroDataProps.help = getZeroDataHelp(isHosted, SharedResources.EmptyQueryHelpText, actId, onFeedbackMailLinkCliked);
            zeroDataProps.announceMessage = SharedResources.EmptyQueryMessage;
        } else if (zeroDataProps.scenario === ZeroData.ScenarioType.OnlyWildcardQueryNotSupported) {
            zeroDataProps.message = getZeroDataMessage(queryText, SharedResources.OnlyWildcardQueryNotSupportedMessage);
            zeroDataProps.help = getZeroDataHelp(isHosted, SharedResources.OnlyWildcardQueryNotSupportedHelpText, actId, onFeedbackMailLinkCliked);
            zeroDataProps.announceMessage = SharedResources.OnlyWildcardQueryNotSupportedMessage;
        } else if (zeroDataProps.scenario === ZeroData.ScenarioType.ZeroResultsWithWildcard ||
            zeroDataProps.scenario === ZeroData.ScenarioType.ZeroResultsWithNoWildcardNoFilter) {
            zeroDataProps.message = getZeroDataMessage(queryText, Resources.ZeroPackageResultsWithoutFiltersMessageFormat);
            let helpMessage: string = (zeroDataProps.scenario === ZeroData.ScenarioType.ZeroResultsWithWildcard) ? "" : SharedResources.ZeroResultsWithNoWildcardNoFilterHelpText;
            zeroDataProps.help = getZeroDataHelp(isHosted, helpMessage, actId, onFeedbackMailLinkCliked);
            zeroDataProps.announceMessage = format(Resources.ZeroPackageResultsWithoutFiltersMessageFormat, queryText);
        } else if (zeroDataProps.scenario === ZeroData.ScenarioType.PrefixWildCardNotSupported) {
            zeroDataProps.message = ZeroData.getQueryNotSupportedHelp(queryText, SharedResources.WildCardNotSupportedHelpFormat);
            zeroDataProps.help = SharedResources.WildCardNotSupportedMessage;
            zeroDataProps.announceMessage = format(SharedResources.WildCardNotSupportedHelpFormat, queryText);
        } else if (zeroDataProps.scenario === ZeroData.ScenarioType.NoPermission) {
            zeroDataProps.message = ZeroData.getAccessPermissionHelp();
            zeroDataProps.help = Resources.ZeroPackageResultsNoPermissionMessage;
            zeroDataProps.announceMessage = Resources.ZeroPackageResultsNoPermissionMessage;
        } else if (zeroDataProps.scenario === ZeroData.ScenarioType.IndexingScenario) {
            const feedbackLink = format(Constants.CodexFeedbackMailToLinkFormat, actId);
            zeroDataProps.message = Resources.AccountIndexingMessage;
            zeroDataProps.help = ZeroData.getIndexingHelp(isHosted, feedbackLink);
            zeroDataProps.announceMessage = Resources.AccountIndexingMessage;
        }
    }

    return zeroDataProps;
}

function getZeroDataMessage(
    queryText: string,
    messageText: string): JSX.Element {
    const message: JSX.Element =
        <FormatComponent format={messageText}>
            {
                <span className="searchText">
                    {queryText}
                </span>
            }
        </FormatComponent>;

    return message;
}

function getZeroDataHelp(
    isHosted: boolean,
    helpText: string,
    activityId: string | undefined,
    notifyFeedbackMailLinkClicked: () => void
): JSX.Element {
    const feedbackLink = format(Constants.CodexFeedbackMailToLinkFormat, activityId);
    const learnMoreLinkAriaLabel = format(SharedResources.LearnMoreAriaLabel, Resources.AritfactsSearchAriaLabel);
    const help: JSX.Element = ZeroData.getZeroResultsHelpComponent(
        isHosted,
        helpText,
        Resources.ZeroPackageResultsLearnMoreFormat,
        Constants.LearnMoreLink,
        learnMoreLinkAriaLabel,
        feedbackLink,
        notifyFeedbackMailLinkClicked);

    return help;
}